/**
 * Serviço para buscar dados do ZARC do Supabase
 * (Zoneamento Agrícola de Risco Climático)
 */

import { supabase } from '../supabaseClient';
import { culturaParaZARC, obterInfoCultura } from '../constants/culturas';

// ==============================================================================
// FUNÇÕES AUXILIARES
// ==============================================================================

/**
 * Processa os dados brutos do Supabase e extrai decêndios
 */
function processarDadosZARC(dadosSupabase) {
  if (!dadosSupabase) return null;
  
  // Extrair os 36 decêndios (colunas '1' a '36')
  const decendios = [];
  const decendiosIdeais = [];
  const decendiosAdequados = [];
  const decendiosAtencao = [];
  
  for (let i = 1; i <= 36; i++) {
    const valor = dadosSupabase[i.toString()];
    decendios.push(valor);
    
    if (valor === 20 || valor === '20') {
      decendiosIdeais.push(i);
    } else if (valor === 30 || valor === '30') {
      decendiosAdequados.push(i);
    } else if (valor === 40 || valor === '40') {
      decendiosAtencao.push(i);
    }
  }
  
  // Identificar o melhor decêndio (menor risco)
  let melhorDecendio = null;
  let melhorRisco = 100;
  
  for (let i = 1; i <= 36; i++) {
    const valor = parseInt(dadosSupabase[i.toString()]);
    if (!isNaN(valor) && valor < melhorRisco) {
      melhorRisco = valor;
      melhorDecendio = i;
    }
  }
  
  // Converter para data específica
  const melhorPeriodo = melhorDecendio ? decendioParaData(melhorDecendio) : null;
  
  // Converter decêndios em meses
  const mesesIdeais = decendiosParaMeses(decendiosIdeais);
  const mesesAdequados = decendiosParaMeses(decendiosAdequados);
  const mesesAtencao = decendiosParaMeses(decendiosAtencao);
  
  return {
    code_muni: dadosSupabase.code_muni,
    cultura: dadosSupabase.Cultura || dadosSupabase.cultura,
    decendios,
    decendiosIdeais: decendiosIdeais.join(','),
    decendiosAdequados: decendiosAdequados.join(','),
    decendiosAtencao: decendiosAtencao.join(','),
    totalIdeais: decendiosIdeais.length,
    totalAdequados: decendiosAdequados.length,
    totalAtencao: decendiosAtencao.length,
    mesesIdeais,
    mesesAdequados,
    mesesAtencao,
    melhorDecendio,
    melhorPeriodo,
    melhorRisco,
    nivelGeral: calcularNivelGeral(decendiosIdeais.length, decendiosAdequados.length)
  };
}

/**
 * Converte lista de decêndios em nomes de meses
 */
function decendiosParaMeses(decendios) {
  if (!decendios || decendios.length === 0) return '';
  
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
                 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  
  const mesesUnicos = new Set();
  decendios.forEach(d => {
    const mesIndex = Math.floor((d - 1) / 3);
    if (mesIndex >= 0 && mesIndex < 12) {
      mesesUnicos.add(meses[mesIndex]);
    }
  });
  
  return Array.from(mesesUnicos).join(', ');
}

/**
 * Converte número do decêndio para data específica
 */
export function decendioParaData(decendio) {
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  const mesIndex = Math.floor((decendio - 1) / 3);
  const decendioNoMes = ((decendio - 1) % 3) + 1; // 1, 2 ou 3
  
  if (mesIndex < 0 || mesIndex >= 12) return null;
  
  const mes = meses[mesIndex];
  
  // Calcular dias do decêndio
  let diaInicio, diaFim;
  if (decendioNoMes === 1) {
    diaInicio = 1;
    diaFim = 10;
  } else if (decendioNoMes === 2) {
    diaInicio = 11;
    diaFim = 20;
  } else { // decendioNoMes === 3
    diaInicio = 21;
    // Último decêndio varia por mês
    const diasNoMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    diaFim = diasNoMes[mesIndex];
  }
  
  return {
    decendio,
    mes,
    mesAbrev: mes.substring(0, 3),
    mesIndex: mesIndex + 1,
    decendioNoMes,
    diaInicio,
    diaFim,
    periodo: `${diaInicio}-${diaFim}`,
    descricao: `${mes}, ${diaInicio} a ${diaFim}`,
    descricaoAbrev: `${mes.substring(0, 3)} ${diaInicio}-${diaFim}`
  };
}

/**
 * Calcula nível geral de favorabilidade
 */
function calcularNivelGeral(totalIdeais, totalAdequados) {
  if (totalIdeais >= 12) return 'favoravel';
  if (totalIdeais + totalAdequados >= 15) return 'moderado';
  return 'restrito';
}

// ==============================================================================
// FUNÇÕES DE BUSCA NO SUPABASE
// ==============================================================================

/**
 * Busca dados do ZARC para um município e cultura específicos no Supabase
 * 
 * @param {string} codeMuni - Código IBGE do município (ex: "1200013")
 * @param {string} cultura - Nome da cultura (ex: "Feijão", "Soja")
 * @returns {Promise<Object|null>} Dados processados do ZARC ou null se não encontrado
 */
export async function buscarDadosZARC(codeMuni, cultura) {
  if (!codeMuni || !cultura) {
    console.warn('⚠️ codeMuni e cultura são obrigatórios');
    return null;
  }

  const culturaCatalogada = obterInfoCultura(cultura);
  if (culturaCatalogada?.zarc === false) {
    console.log(`ℹ️ ${culturaCatalogada.label} não possui correspondência direta no ZARC`);
    return null;
  }
  
  try {
    console.log(`🔍 Buscando ZARC para ${cultura} em ${codeMuni}`);
    
    // Normalizar cultura usando mapeamento centralizado
    const culturaNormalizada = culturaParaZARC(cultura);
    console.log(`🔍 Cultura normalizada: "${culturaNormalizada}"`);
    
    // Estratégia 1: Busca exata (case-insensitive)
    let { data, error } = await supabase
      .from('zarc_data')
      .select('*')
      .eq('code_muni', codeMuni)
      .ilike('Cultura', culturaNormalizada)
      .maybeSingle();
    
    // Estratégia 2: Busca com padrão flexível (ILIKE)
    if (!data && !error) {
      console.log(`🔍 Busca exata falhou, tentando busca flexível...`);
      
      const { data: dataFlex, error: errorFlex } = await supabase
        .from('zarc_data')
        .select('*')
        .eq('code_muni', codeMuni)
        .ilike('Cultura', `%${culturaNormalizada}%`);
      
      if (!errorFlex && dataFlex && dataFlex.length > 0) {
        data = dataFlex[0];
        console.log(`✅ Encontrado por busca flexível: ${data.Cultura}`);
        
        if (dataFlex.length > 1) {
          console.log(`ℹ️ Múltiplas correspondências encontradas (${dataFlex.length}):`);
          dataFlex.forEach(d => console.log(`  - ${d.Cultura}`));
          console.log(`  Usando: ${data.Cultura}`);
        }
      } else {
        error = errorFlex;
      }
    }
    
    // Estratégia 3: Se ainda não encontrou, listar culturas disponíveis
    if (!data && !error) {
      console.log(`🔍 Nenhuma correspondência encontrada, listando culturas disponíveis...`);
      
      const { data: todasCulturas } = await supabase
        .from('zarc_data')
        .select('Cultura')
        .eq('code_muni', codeMuni)
        .limit(10);
      
      if (todasCulturas && todasCulturas.length > 0) {
        console.log(`📋 Culturas disponíveis neste município:`);
        todasCulturas.forEach(c => console.log(`  - ${c.Cultura}`));
      } else {
        console.log(`ℹ️ Nenhuma cultura cadastrada para este município`);
      }
    }
    
    if (error) {
      console.error('❌ Erro ao buscar ZARC:', error);
      return null;
    }
    
    if (!data) {
      console.log(`ℹ️ Dados ZARC não encontrados para ${cultura} em ${codeMuni}`);
      return null;
    }
    
    console.log(`✅ Dados ZARC encontrados: ${data.Cultura}`);
    
    // Processar e retornar dados formatados
    return processarDadosZARC(data);
    
  } catch (error) {
    console.error('❌ Erro ao buscar dados ZARC:', error);
    return null;
  }
}

/**
 * Busca dados do ZARC para múltiplas culturas de um município
 * 
 * @param {string} codeMuni - Código IBGE do município
 * @param {string[]} culturas - Array de nomes de culturas
 * @returns {Promise<Object[]>} Array de dados processados do ZARC
 */
export async function buscarDadosZARCMultiplo(codeMuni, culturas) {
  if (!codeMuni || !culturas || culturas.length === 0) {
    console.warn('⚠️ codeMuni e culturas são obrigatórios');
    return [];
  }
  
  try {
    console.log(`🔍 Buscando ZARC para ${culturas.length} culturas em ${codeMuni}`);
    
    const { data, error } = await supabase
      .from('zarc_data')
      .select('*')
      .eq('code_muni', codeMuni)
      .in('Cultura', culturas);
    
    if (error) {
      console.error('❌ Erro ao buscar ZARC múltiplo:', error);
      return [];
    }
    
    console.log(`✅ ${data?.length || 0} culturas com dados ZARC em ${codeMuni}`);
    
    return data ? data.map(processarDadosZARC) : [];
    
  } catch (error) {
    console.error('❌ Erro ao buscar dados ZARC múltiplos:', error);
    return [];
  }
}

/**
 * Lista todas as culturas disponíveis no ZARC
 * 
 * @param {string} codeMuni - Opcional: filtrar por município
 * @returns {Promise<string[]>} Array de nomes de culturas
 */
export async function listarCulturasZARC(codeMuni = null) {
  try {
    let query = supabase
      .from('zarc_data')
      .select('Cultura');
    
    if (codeMuni) {
      query = query.eq('code_muni', codeMuni);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('❌ Erro ao listar culturas ZARC:', error);
      return [];
    }
    
    const culturas = [...new Set(
      data.map(row => row.Cultura)
    )].sort();
    
    console.log(`✅ ${culturas.length} culturas disponíveis no ZARC`);
    return culturas;
    
  } catch (error) {
    console.error('❌ Erro ao listar culturas ZARC:', error);
    return [];
  }
}

/**
 * Verifica se há dados disponíveis para uma cultura e município
 * 
 * @param {string} codeMuni - Código IBGE do município
 * @param {string} cultura - Nome da cultura
 * @returns {Promise<boolean>}
 */
export async function temDadosZARC(codeMuni, cultura) {
  const culturaCatalogada = obterInfoCultura(cultura);
  if (culturaCatalogada?.zarc === false) return false;

  try {
    const { data, error } = await supabase
      .from('zarc_data')
      .select('code_muni')
      .eq('code_muni', codeMuni)
      .ilike('Cultura', cultura)
      .maybeSingle();
    
    if (error) return false;
    return !!data;
    
  } catch (error) {
    console.error('❌ Erro ao verificar dados ZARC:', error);
    return false;
  }
}

// ==============================================================================
// EXPORTAÇÕES
// ==============================================================================

export default {
  buscarDadosZARC,
  buscarDadosZARCMultiplo,
  listarCulturasZARC,
  temDadosZARC,
  decendioParaData
};
